import { atom } from "jotai";

// Mirrors order-handler src/data/interfaces/interior_project.ts

// #region InteriorProjectStatus
export type InteriorProjectStatus = 'DRAFT' | 'SHARED' | 'SELECTIONS_COMPLETE' | 'ORDERED';

// #region InteriorProject
export interface InteriorProject extends InteriorProjectShipTo {
    id?: number;
    designer_brand_id: number;
    client_name: string;
    client_email?: string | null;
    status: InteriorProjectStatus;
    // Active share link (present on the detail read when the project is shared
    // and the token hasn't expired) — lets the Share modal show + copy it.
    share_url?: string | null;
    // Native-order reference — set when the designer confirms the project.
    ordered_order_number?: number | null;
    ordered_queue_id?: number | null;
    created_at?: string;
    updated_at?: string;
}

// #region MockupStatus
export type MockupStatus = 'PENDING' | 'GENERATED' | 'FAILED';

// #region HydratedWallSelectedDesign
// Homeowner selection hydrated with its AI mockup status + short-lived signed URL.
// Mirrors order-handler HydratedWallSelectedDesign.
export interface HydratedWallSelectedDesign {
    id?: number;
    wall_id: number;
    recommended_design_id: number;
    design_id: number;
    mockup_status?: MockupStatus;
    // Computed at read time — expires ~1 h after issue.
    ai_mockup_signed_url?: string | null;
    created_at?: string;
    updated_at?: string;
}

// #region ApiOrderShippingAddress
// Mirrors order-handler ApiOrderShippingAddress — NOTE: uses `state`, not `province`.
export interface ApiOrderShippingAddress {
    first_name?: string;
    last_name?: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
}

// #region ConfirmOrderPayload
// Body for POST /interior-projects/:projectId/confirm. No phone field (backend
// derives customer name/email from the project and does not accept phone).
export interface ConfirmOrderPayload {
    shipping_address: ApiOrderShippingAddress;
    billing_address?: ApiOrderShippingAddress;
    notes?: string;
}

// #region ConfirmOrderResult
export interface ConfirmOrderResult {
    order_number: number;
    // Amount billed to the designer's brand (interior_designer_price × sqft).
    charge_amount?: number;
}

// #region OrderChargePreview
// Read-only estimate of the interior-designer charge, shown before placing.
export interface OrderChargePreview {
    charge_amount: number;
    rate: number;
    total_sqft: number;
}

// #region ConfirmWallIssue
// A single wall's readiness breakdown from a backend 400 (trace.walls[]).
export interface ConfirmWallIssue {
    wall_id: number;
    wall_name: string;
    issues: string[];
}

// #region InteriorWall
export interface InteriorWall {
    id?: number;
    project_id: number;
    name: string;
    wall_width?: number | null;
    wall_height?: number | null;
    material_id?: number | null;
    wall_photo_key?: string | null;
    // Computed at read time from wall_photo_key — expires ~1 h after issue.
    wall_photo_signed_url?: string | null;
    created_at?: string;
    updated_at?: string;
}

// #region WallRecommendedDesign
// Hydrated recommendation as returned by the detail aggregate. Carries its AI
// mockup (generated at share time): status + short-lived signed URL.
export interface WallRecommendedDesign {
    id?: number;
    wall_id: number;
    design_id: number;
    rank: number;
    title: string | null;
    thumbnail_url: string | null;
    mockup_status?: MockupStatus;
    // Computed at read time — expires ~1 h after issue.
    ai_mockup_signed_url?: string | null;
    created_at?: string;
}

// #region InteriorWallDetail
export interface InteriorWallDetail extends InteriorWall {
    recommended_designs: WallRecommendedDesign[];
    // Homeowner selection(s) with per-selection AI mockup status/URL — at most
    // one per wall. Undefined until the backend hydrates them on the read path.
    selections?: HydratedWallSelectedDesign[];
}

// #region InteriorProjectShipTo
// The client's ship-to for the project. Set once, auto-filled into every
// shipment the project produces (sample bundles and the final wall order).
export interface InteriorProjectShipTo {
    // Recipient on the shipping label — collected with the address, independent
    // of the project's client_name.
    ship_first_name?: string | null;
    ship_last_name?: string | null;
    ship_address_1?: string | null;
    ship_address_2?: string | null;
    ship_city?: string | null;
    ship_state?: string | null;
    ship_zip?: string | null;
    ship_country?: string | null;
    ship_phone?: string | null;
}

// #region InteriorProjectDesign
// An entry on the project-wide design shortlist — the set of possibilities the
// designer committed to for the whole project, before walls exist. Walls draw
// their recommendations from this list.
export interface InteriorProjectDesign {
    id?: number;
    project_id: number;
    design_id: number;
    rank: number;
    title: string | null;
    thumbnail_url: string | null;
    pattern_type: string | null;
    created_at?: string;
}

// #region ProjectDesignConflict
// A shortlist design the backend refused to drop because the client already
// selected it (409 trace.designs[]).
export interface ProjectDesignConflict {
    design_id: number;
    title: string | null;
    wall_ids: number[];
    wall_names: string[];
}

// #region SaveProjectDesignsFailure
// Structured 409 from PUT /:projectId/designs so the UI can name the walls.
export interface SaveProjectDesignsFailure {
    error: true;
    message: string;
    conflicts: ProjectDesignConflict[];
}

// #region InteriorProjectDetail
export interface InteriorProjectDetail extends InteriorProject {
    // Project-wide design shortlist (step 2 of the designer flow).
    designs: InteriorProjectDesign[];
    walls: InteriorWallDetail[];
}

// #region InteriorProjectListResponse
export interface InteriorProjectListResponse {
    projects: InteriorProject[];
    total: number;
}

// #region InteriorProjectUpdatePayload
// Partial update — send only the keys you intend to change. The backend treats a
// present key as "change this", so omitting client_name leaves the name alone.
export interface InteriorProjectUpdatePayload extends InteriorProjectShipTo {
    client_name?: string;
    client_email?: string | null;
}

// #region InteriorWallPayload
export interface InteriorWallPayload {
    name?: string;
    wall_width?: number | null;
    wall_height?: number | null;
    material_id?: number | null;
}

// Project list state
export const projectsAtom = atom<InteriorProject[]>([]);
export const projectsTotalAtom = atom<number>(0);
export const projectsLoadingAtom = atom<boolean>(false);

// Project detail state
export const projectDetailAtom = atom<InteriorProjectDetail | null>(null);
export const projectDetailLoadingAtom = atom<boolean>(false);
